/**
 * Turning an `ApiError` into a sentence the user should read.
 *
 * The backend's `message` field is operator-facing: one language (mixed RU/EN
 * across services), phrased for whoever reads the logs — "invalid input",
 * "restaurant not found". Showing it verbatim is what the panels used to do,
 * and it is why a Uzbek-speaking merchant could be told `ErrProductNotFound`.
 *
 * So the numeric `code` is the source of truth for wording, and the backend
 * message is demoted to a last resort. Resolution order, per call:
 *
 *   1. the app's translation of `errors:<key>` for this code
 *   2. the caller's own fallback (already translated at the call site)
 *   3. the backend's message
 *
 * Step 1 needs a translator, which lives in `@noor/lib`/the app — packages this
 * one sits BELOW and must not import. It is injected at boot instead, exactly
 * like `setLocaleResolver` in `locale.ts`.
 */
import { isApiError } from './error';
import { errorCodeKey } from './error-catalog';

/**
 * Translates an `errors`-namespace key. Returns `undefined` when the app has no
 * string for it, so resolution falls through to the caller's fallback rather
 * than rendering a raw key like `errors:product.notFound` at the user.
 */
export type ErrorTranslator = (key: string) => string | undefined;

let translate: ErrorTranslator | null = null;

/**
 * Point the error layer at the app's i18n. Called once during boot with a
 * FUNCTION — not a snapshot of strings — so a language switch is picked up by
 * the next error with nothing to re-register.
 */
export function setErrorTranslator(translator: ErrorTranslator): void {
  translate = translator;
}

/**
 * The translated message for a backend error code, if this build knows the code
 * AND the app has a string for it. `undefined` otherwise.
 *
 * Exported for the rare caller that wants the code's own wording independent of
 * an `ApiError` — a retry banner deciding its copy, say. Most code should use
 * `getApiErrorMessage`.
 */
export function getErrorCodeMessage(
  code: string | number | undefined,
): string | undefined {
  const key = errorCodeKey(code);
  if (key === undefined) return undefined;
  const message = translate?.(key);
  return message !== undefined && message.trim() !== '' ? message : undefined;
}

/**
 * Whether `error` is an `ApiError` whose code this catalog recognizes. Useful
 * for branching on a specific failure — e.g. treating 4605
 * (`changeRequest.alreadyPending`) as "open the pending request" rather than an
 * error toast.
 */
export function hasKnownErrorCode(error: unknown): boolean {
  return isApiError(error) && errorCodeKey(error.code) !== undefined;
}
